import { create } from 'zustand';
import { ROLE_RANK } from '@sbg/shared/constants';
import type { Role } from '@sbg/shared/constants';
import type { UserDto } from '@sbg/shared/schemas';

/**
 *  idle          — nothing asked yet (SSR and the first client render)
 *  loading       — `GET /auth/me` in flight
 *  authenticated — `user` is set
 *  anonymous     — no session (or it expired)
 */
export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'anonymous';

interface AuthState {
  user: UserDto | null;
  status: AuthStatus;
  setUser: (user: UserDto | null) => void;
  setStatus: (status: AuthStatus) => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  status: 'idle',
  setUser: (user) => {
    set({ user, status: user ? 'authenticated' : 'anonymous' });
  },
  setStatus: (status) => {
    set({ status });
  },
}));

/** UI-only check (the API enforces every rule again): does `role` reach at least `minimum`? */
export const hasRoleAtLeast = (role: Role | undefined, minimum: Role): boolean =>
  role !== undefined && ROLE_RANK[role] >= ROLE_RANK[minimum];

export interface AuthView {
  user: UserDto | null;
  status: AuthStatus;
  /** Session state is still being determined. */
  pending: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

export function useAuth(): AuthView {
  const user = useAuthStore((state) => state.user);
  const status = useAuthStore((state) => state.status);
  return {
    user,
    status,
    pending: status === 'idle' || status === 'loading',
    isAuthenticated: status === 'authenticated',
    isAdmin: hasRoleAtLeast(user?.role, 'ADMIN'),
    isSuperAdmin: hasRoleAtLeast(user?.role, 'SUPER_ADMIN'),
  };
}
